import SpaceLogin from 'assets/img/2026/estrelas_fundo.png';
import logoPremioBB from 'assets/img/2026/logo_premio.png';
import logoPremioPrincipal from 'assets/img/2026/logo_premio_page.png';
import personagemPerfil from 'assets/img/2026/personagem_perfil_BB.png';
import { SimpleHeader } from 'components/ui/SimpleHeader';
import { AuthContext } from 'context/AuthContext';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { recoverPassword, registerBanca, validateBanca, validateUser } from 'services/auth.service';
import './style.scss';
import { ativarModoSimulacao } from 'utils/modo-simulacao';

type LoginStep = 'landing' | 'email' | 'register' | 'login';

interface ValidatedUser {
  name: string;
  email: string;
  _id: string;
  hasPassword?: boolean;
  password?: string;
}

const OTP_TOTAL_SECONDS = 59;

export function LoginPublic() {
  const navigate = useNavigate();
  const location = useLocation();
  const isBanca = location.pathname === '/login';
  const { loginWithToken, login, onRegisterUser } = useContext(AuthContext);

  const [autoLoading, setAutoLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<LoginStep>('landing');
  const [errorMessage, setErrorMessage] = useState('');

  // Step 1 - Email
  const [emailInput, setEmailInput] = useState('');

  // Step 2 - Register
  const [validatedUser, setValidatedUser] = useState<ValidatedUser | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '']);
  const [otpCountdown, setOtpCountdown] = useState(OTP_TOTAL_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const [otpTimerKey, setOtpTimerKey] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Step 3 - Login
  const [loginInput, setLoginInput] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Forgot password (banca) — reuses the 'register' step UI but always asks for OTP
  const [forgotMode, setForgotMode] = useState(false);

  // URL param auto-login
  useEffect(() => {
    async function doLogin(username: string, pwd: string) {
      try {
        await login({ username, password: pwd });
        setTimeout(() => navigate('/'), 150);
      } catch {
        setAutoLoading(false);
      }
    }

    async function doLoginWithToken(token: string) {
      try {
        await loginWithToken(token);
        setTimeout(() => navigate('/'), 150);
      } catch {
        setAutoLoading(false);
      }
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const agent = params.get('agent');
    const secret = params.get('secret');
    const tokenItem = params.get('tokenItem');

    if (token) {
      setAutoLoading(true);
      doLoginWithToken(token);
    } else if (agent && secret) {
      setAutoLoading(true);
      doLogin(agent, secret);
    } else if (tokenItem) {
      setAutoLoading(true);
      const decoded = atob(tokenItem);
      const [id, pwd] = decoded.split(':');
      ativarModoSimulacao();
      doLogin(id, pwd);
    }
  }, [loginWithToken, login, navigate]);

  // OTP countdown timer
  useEffect(() => {
    if (step !== 'register') return;
    setOtpCountdown(OTP_TOTAL_SECONDS);
    setCanResend(false);

    const timer = setInterval(() => {
      setOtpCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [step, otpTimerKey]);

  async function handleEmailSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    if (!emailInput.trim()) return;
    setLoading(true);
    setErrorMessage('');
    try {
      const email = emailInput.trim().toLowerCase();
      const result = isBanca
        ? await validateBanca({ email })
        : await validateUser({ email } as any);
      if (result) {
        setValidatedUser(result);
        if (result.hasPassword || result.password) {
          setLoginInput(result._id || email);
          setStep('login');
        } else {
          if (!isBanca) {
            try { await recoverPassword(email); } catch {}
          }
          setStep('register');
        }
      }
    } catch (e: any) {
      setErrorMessage(e?.message || 'Email não encontrado ou não autorizado.');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPasswordBanca() {
    if (!isBanca) return;
    const email = (loginInput || emailInput).trim().toLowerCase();
    if (!email) {
      setErrorMessage('Informe o email para recuperar a senha.');
      return;
    }
    setLoading(true);
    setErrorMessage('');
    try {
      const result = await validateBanca({ email, forgot: true });
      if (result) {
        setValidatedUser(result);
        setForgotMode(true);
        setPassword('');
        setConfirmPassword('');
        setOtpDigits(['', '', '', '', '']);
        setStep('register');
      }
    } catch (e: any) {
      setErrorMessage(e?.message || 'Não foi possível enviar o código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendOtp() {
    if (!canResend) return;
    const target = validatedUser?.email || emailInput.trim().toLowerCase();
    try {
      if (isBanca) {
        await validateBanca({ email: target, forgot: forgotMode || undefined });
      } else {
        await recoverPassword(target);
      }
    } catch {}
    setOtpDigits(['', '', '', '', '']);
    setOtpTimerKey(k => k + 1);
    setTimeout(() => otpRefs.current[0]?.focus(), 50);
  }

  function handleOtpInput(index: number, value: string) {
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);
    if (value && index < 4) {
      otpRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpPaste(index: number, e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5 - index);
    if (!pasted) return;
    const newDigits = [...otpDigits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[index + i] = pasted[i];
    }
    setOtpDigits(newDigits);
    const lastIdx = Math.min(index + pasted.length - 1, 4);
    const nextIdx = Math.min(index + pasted.length, 4);
    const focusTarget = pasted.length === 5 - index ? lastIdx : nextIdx;
    otpRefs.current[focusTarget]?.focus();
  }

  function handleOtpKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  async function handleRegisterSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setErrorMessage('');
    if (password !== confirmPassword) {
      setErrorMessage('As senhas não coincidem.');
      return;
    }
    const code = otpDigits.join('');
    if (code.length < 5) {
      setErrorMessage('Digite o código de 5 dígitos enviado para o seu email.');
      return;
    }
    setLoading(true);
    try {
      if (isBanca) {
        await registerBanca({
          _id: validatedUser!.email,
          password,
          code,
        });
        setLoginInput(validatedUser!.email);
        setLoginPassword('');
        setPassword('');
        setConfirmPassword('');
        setOtpDigits(['', '', '', '', '']);
        setErrorMessage('');
        setStep('login');
      } else {
        await onRegisterUser({
          _id: validatedUser!._id,
          name: validatedUser!.name,
          email: validatedUser!.email,
          password,
          extra: { code },
        });
        navigate('/');
      }
    } catch (e: any) {
      setErrorMessage(e?.message || 'Erro ao cadastrar. Verifique o código e tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  async function handleLoginSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);
    try {
      // Marca explicitamente a sessão como banca técnica para que o signOut
      // possa redirecionar para login sem depender de heurística do _id.
      try {
        if (isBanca) {
          window.localStorage.setItem('bb.banca-session', '1');
        } else {
          window.localStorage.removeItem('bb.banca-session');
        }
      } catch {
        /* ignora */
      }
      await login({ username: loginInput, password: loginPassword }, 'senha');
      navigate('/');
    } catch {
      setErrorMessage('Login ou senha incorretos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function formatCountdown(seconds: number) {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  if (autoLoading) {
    return (
      <div className="login-public-bg flex items-center justify-center w-screen h-screen">
        <span className="text-white text-xl font-bold">CARREGANDO...</span>
      </div>
    );
  }

  // ── Landing screen (original design) ──────────────────────────────────────
  if (step === 'landing') {
    return (
      <div id="login-public-wrapper">
        <div className="relative flex flex-col w-screen h-screen bg-gradient-to-br from-blue-bb via-blue-bb-1 to-blue-bb-5 overflow-hidden">
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <img src={SpaceLogin} alt="" className="w-full h-full object-cover" />
          </div>

          <div className="absolute top-0 left-0 right-0 z-50">
            <SimpleHeader title="Prêmio BB" userName="" customLogo={logoPremioBB} />
          </div>

          <main className="flex flex-1 items-center justify-center px-4 md:px-8 lg:px-12 relative pt-20 pb-4">
            <div className="flex flex-col items-center justify-center max-w-5xl z-10 w-full login-content">
              <div tabIndex={0} className="text-center mb-3 space-y-0.5">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-pink-bb leading-tight">
                  <span className="block">A grande jornada com iniciativas</span>
                  <span className="block">transformadoras e inspiradoras começou.</span>
                </h1>
                <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-blue-bb-6 leading-tight mt-1">
                  {isBanca ? (
                    <>A sua participação como <span className="text-yellow-bb">avaliador técnico</span> é fundamental!</>
                  ) : (
                    <>Acompanhe sua iniciativa no <span className="text-yellow-bb">Prêmio BB!</span></>
                  )}
                </p>
              </div>

              <div className="flex justify-center mb-4">
                <button
                  onClick={() => setStep('email')}
                  className="btn-iniciar-jornada"
                >
                  ENTRAR
                </button>
              </div>

              <div
                tabIndex={0}
                aria-label="Logo do Prêmio e Personagem puxando a manivela"
                className="relative flex items-end justify-center images-container"
              >
                <div className="relative z-20">
                  <img
                    src={personagemPerfil}
                    alt=""
                    aria-hidden="true"
                    className="h-40 md:h-48 lg:h-60 w-auto object-contain"
                  />
                </div>
                <div className="relative z-10 -ml-6 md:-ml-8 lg:-ml-10">
                  <img
                    src={logoPremioPrincipal}
                    alt="Logo Prêmio BB"
                    className="h-56 md:h-64 lg:h-80 w-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── Email / Register / Login form screens ─────────────────────────────────
  return (
    <div id="login-public-wrapper">
      <div className="login-public-bg flex flex-col w-screen min-h-screen px-8 py-10 md:px-16 md:py-14">
        <div className="mb-6 login-intro">
          <h1 className="text-2xl md:text-3xl font-bold leading-tight">
            <span className="text-pink-bb">Seja bem-vindo ao </span>
            <span className="text-yellow-bb">Prêmio BB!</span>
          </h1>
          <p className="text-2xl md:text-3xl font-bold text-white leading-tight mt-1">
            Faça seu login:
          </p>
        </div>

        <div className="login-card">
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} noValidate>
              <div className="login-field">
                <label className="login-label">Email</label>
                <input
                  type="email"
                  className="login-input"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </div>
              {errorMessage && <p className="login-error">{errorMessage}</p>}
              <div className="login-actions-right">
                <button type="submit" className="login-btn-yellow" disabled={loading}>
                  {loading ? 'ENVIANDO...' : 'ENVIAR'}
                </button>
              </div>
            </form>
          )}

          {step === 'register' && (
            <form onSubmit={handleRegisterSubmit} noValidate>
              <div className="login-field">
                <label className="login-label">Nome completo</label>
                <p className="login-readonly-value">{validatedUser?.name}</p>
              </div>
              <div className="login-field">
                <label className="login-label">Email</label>
                <p className="login-readonly-value">{validatedUser?.email}</p>
              </div>
              <div className="login-field">
                <label className="login-label">Crie uma senha</label>
                <input
                  type="password"
                  className="login-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="login-field">
                <label className="login-label">Confirmar senha</label>
                <input
                  type="password"
                  className="login-input"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="otp-section">
                <p className="otp-message">Um código de 5 dígitos foi enviado para o seu email</p>
                <div className="otp-boxes">
                  {otpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => { otpRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className="otp-box"
                      value={digit}
                      onChange={e => handleOtpInput(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      onPaste={e => handleOtpPaste(i, e)}
                      disabled={loading}
                    />
                  ))}
                </div>
                <p className="otp-resend">
                  Não recebeu?{' '}
                  {canResend ? (
                    <button type="button" onClick={handleResendOtp} className="otp-resend-link">
                      Reenviar código
                    </button>
                  ) : (
                    <span className="otp-resend-link">
                      Reenviar código em {formatCountdown(otpCountdown)}
                    </span>
                  )}
                </p>
              </div>
              {errorMessage && <p className="login-error text-center">{errorMessage}</p>}
              <div className="login-actions-center">
                <button type="submit" className="login-btn-yellow" disabled={loading}>
                  {loading ? 'CADASTRANDO...' : 'CADASTRAR'}
                </button>
              </div>
            </form>
          )}

          {step === 'login' && (
            <form onSubmit={handleLoginSubmit} noValidate>
              <div className="login-field">
                <label className="login-label">Login</label>
                <input
                  type="text"
                  className="login-input"
                  value={loginInput}
                  onChange={e => setLoginInput(e.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </div>
              <div className="login-field">
                <label className="login-label">Senha</label>
                <input
                  type="password"
                  className="login-input"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              {errorMessage && <p className="login-error">{errorMessage}</p>}
              {isBanca && (
                <p className="otp-resend">
                  <button
                    type="button"
                    onClick={handleForgotPasswordBanca}
                    className="otp-resend-link"
                    disabled={loading}
                  >
                    Esqueci minha senha
                  </button>
                </p>
              )}
              <div className="login-actions-right">
                <button type="submit" className="login-btn-yellow" disabled={loading}>
                  {loading ? 'ENTRANDO...' : 'ENTRAR'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
